import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { triggerDownload } from '../../download/trigger-download';
import type { DownloadLinkResponse } from '../../fetchers/job-architecture/job-architecture-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { useTrackExport } from '../hooks/analytics/use-track-export';

type UsePdfExportParams = {
    readonly jobArchitectureId: string;
    readonly jobArchitectureItemId: string;
    readonly jobArchitectureItemType: 'job-level' | 'job-role';
    readonly languageCode: string;
    readonly version?: number;
};

type PdfExportResult = {
    readonly exportPdf: () => Promise<void>;
    readonly isLoading: boolean;
};

export const usePdfExport = (params: UsePdfExportParams): PdfExportResult => {
    const { jobArchitectureId, jobArchitectureItemId, jobArchitectureItemType, languageCode, version } = params;
    const { translate } = useContext(ApplicationContext);
    const { exportJobLevel, exportJobRole } = useApplicationFetchers();
    const { enqueueSnackbar } = useSnackbar();
    const { showSuccessSnackbar } = useSnackbars();
    const { trackExport } = useTrackExport({ architectureId: jobArchitectureId });

    const showErrorMessage = (message: string): void => {
        enqueueSnackbar(message, { variant: 'error' });
    };

    const mutation: UseMutationResult<DownloadLinkResponse, Error, void> = useMutation({
        mutationFn: async () => {
            if (is.nullOrUndefined(jobArchitectureId) || is.nullOrUndefined(jobArchitectureItemId)) {
                throw new Error(`Job architecture ID or ${jobArchitectureItemType} ID is missing`);
            }

            const result = await (jobArchitectureItemType === 'job-role'
                ? exportJobRole({
                      jobArchitectureId,
                      jobRoleId: jobArchitectureItemId,
                      languageCode,
                      version,
                  })
                : exportJobLevel({
                      jobArchitectureId,
                      jobLevelId: jobArchitectureItemId,
                      languageCode,
                      version,
                  }));

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        onError: () => {
            showErrorMessage(translate('pages.job-architecture.export-pdf-error'));
        },
        onSuccess: (data) => {
            showSuccessSnackbar('success.shortlist.exported');
            triggerDownload(data.downloadLink);
        },
    });

    return {
        exportPdf: async () => {
            await mutation.mutateAsync();
            trackExport({
                exportFormat: 'pdf',
                exportScope: jobArchitectureItemType === 'job-role' ? 'role' : 'level',
                levelId: jobArchitectureItemType === 'job-level' ? jobArchitectureItemId : undefined,
                roleId: jobArchitectureItemType === 'job-role' ? jobArchitectureItemId : undefined,
            });
        },
        isLoading: mutation.isPending,
    };
};
